import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { CircularProgress } from "@mui/material";
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { useBonusApplications } from "../../hooks/useBonusQueries";
import { ApplicationItem } from "../../components/ApplicationItem";
import { Breadcrumb } from "../../components/Breadcrumb";
import { HOME_CONSTANTS } from "./constants";
import { RoleType } from "../../components/CreateButton/constants";
import { BonusQueryParams, BonusApplication } from "../../services/bonus/types";
import { getBonusRecipientSearchBlob } from "../../utils/bonusRecipientSearch";
import {
  applicationStatusMatchesFilter,
  getApplicationStatusString,
} from "../../utils/applicationStatusFilter";
import ClientSearchFilters, {
  ClientSearchFiltersState,
  ClientSearchFilterOption,
} from "../../components/ClientSearchFilters";
import ClientPagination from "../../components/ClientPagination";

const HOME_FILTERS_KEY = "homeApplicationFilters";

const Home: React.FC = () => {
  const { accounts } = useMsal();
  const username = accounts[0]?.username || HOME_CONSTANTS.DEFAULT_USER.USERNAME;
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const navigate = useNavigate();
  const selectedRole = localStorage.getItem("selectedRole") || "";
  const normalizedRole = selectedRole.toUpperCase();
  const isDccmRole = normalizedRole === "DCCM" || normalizedRole === "DCCM-REVIEW";
  const isDmRole = normalizedRole === "DM" || normalizedRole === "DM-REVIEW";

  const isCCM3Role = normalizedRole === "CCM3";

  const lockedApplicationType = ["CCM1", "CCM2", "CCM3Manager", "CM3", "PM", "SPM"].includes(normalizedRole)
    ? normalizedRole
    : null;
  const dccmApplicationTypeOptions: ClientSearchFilterOption[] = [
    { value: "AllCustomerCare", label: "All Customer Care" },
    { value: "CCM1", label: "CCM1" },
    { value: "CCM2", label: "CCM2" },
    { value: "CCM3", label: "CCM3" },
    { value: "CCM3Manager", label: "CCM3 Manager" },
    { value: "DCCM", label: "DCCM" },
  ];
  const dccmMyApplicationsStatusOptions: ClientSearchFilterOption[] = [
    { value: "All_Status", label: "All Status" },
    { value: "Bonus_Recipient_Review", label: "Recipient Review" },
  ];
  const dmApplicationTypeOptions: ClientSearchFilterOption[] = [
    { value: "AllConstruction", label: "All Construction" },
    { value: "CM3", label: "CM3" },
    { value: "PM", label: "PM" },
    { value: "SPM", label: "SPM" },
  ];

  const getInitialFilters = (): BonusQueryParams => {
    return {
      year: "All",
      quarter: "All",
    };
  };

  const getInitialClientFilters = (): ClientSearchFiltersState => {
    const stored = localStorage.getItem(HOME_FILTERS_KEY);
    if (stored) {
      try {
        return JSON.parse(stored) as ClientSearchFiltersState;
      } catch (e) {
        console.error("Failed to parse stored home filters:", e);
      }
    }
    return {
      year: "All",
      quarter: "All",
      applicationType: isDccmRole
        ? "AllCustomerCare"
        : isDmRole
          ? "AllConstruction"
          : isCCM3Role
            ? "AllCCM3Types"
            : lockedApplicationType ?? "All",
      status: isDccmRole ? "All_Status" : "All",
      bonusRecipient: "",
      region: "All",
      pageSize: 25,
    };
  };

  const [filters, setFilters] = useState<BonusQueryParams>(getInitialFilters);
  const [clientFilters, setClientFilters] = useState<ClientSearchFiltersState>(getInitialClientFilters);
  const [appliedClientFilters, setAppliedClientFilters] = useState<ClientSearchFiltersState>(getInitialClientFilters);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setFilters({
      year: clientFilters.year === "All" ? "All" : clientFilters.year,
      quarter: clientFilters.quarter === "All" ? "All" : clientFilters.quarter,
    });
  }, [clientFilters.year, clientFilters.quarter]);

  useEffect(() => {
    localStorage.setItem(HOME_FILTERS_KEY, JSON.stringify(clientFilters));
  }, [clientFilters]);


  const {
    data: apiBonusApplicationsResponse,
    isLoading,
    error,
  } = useBonusApplications(filters);

  const applications: BonusApplication[] = apiBonusApplicationsResponse?.data || [];

  const KNOWN_REGIONS = ["Houston", "Austin", "San Antonio", "DFW", "Other"];
  const getRegion = (app: any): string => app.location || "";

  const filteredApplications = applications.filter((app) => {
    const appType = app.applicationType || "";
    const bonusRecipientBlob = getBonusRecipientSearchBlob(app);
    const region = getRegion(app);

    const appTypeMatch =
      appliedClientFilters.applicationType === "All" ||
      (appliedClientFilters.applicationType === "AllCCM3Types" && ["CCM3", "CCM3Manager"].includes(appType)) ||
      (appliedClientFilters.applicationType === "AllConstruction" && ["CM3", "PM", "SPM"].includes(appType)) ||
      (appliedClientFilters.applicationType === "AllCustomerCare" && ["CCM1", "CCM2", "CCM3", "CCM3Manager", "DCCM"].includes(appType)) ||
      appType === appliedClientFilters.applicationType;

    const statusMatch = applicationStatusMatchesFilter(
      getApplicationStatusString(app),
      appliedClientFilters.status
    );

    const bonusRecipientMatch =
      !appliedClientFilters.bonusRecipient ||
      bonusRecipientBlob.includes(appliedClientFilters.bonusRecipient.toLowerCase());

    const regionMatch =
      appliedClientFilters.region === "All" ||
      (appliedClientFilters.region === "Other"
        ? !KNOWN_REGIONS.includes(region) && region !== ""
        : region === appliedClientFilters.region);
    const yearMatch =
      appliedClientFilters.year === "All" || app.year === appliedClientFilters.year;
    const quarterMatch =
      appliedClientFilters.quarter === "All" || app.quarter === appliedClientFilters.quarter;

    return appTypeMatch && statusMatch && bonusRecipientMatch && regionMatch && yearMatch && quarterMatch;
  });

  const paginatedApplications = filteredApplications.slice(
    (page - 1) * appliedClientFilters.pageSize,
    page * appliedClientFilters.pageSize
  );

  const breadcrumbPages = [
    {
      name: HOME_CONSTANTS.BREADCRUMB.TITLE,
      href: HOME_CONSTANTS.BREADCRUMB.PATH,
      current: true,
      icon: (props: React.ComponentProps<"svg">) => (
        <ClipboardDocumentIcon
          {...props}
          className="w-6 h-6 text-primary-500 transition duration-75 group-hover:text-gray-900"
        />
      ),
    },
  ];

  const toggleRowExpansion = (id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="xl:pl-0 flex-grow flex flex-col">
        <main className="flex-grow flex flex-col overflow-hidden">
          <div className="relative isolate overflow-hidden pt-6 flex-grow flex flex-col">
            <div
              className={`mx-auto w-full ${HOME_CONSTANTS.LAYOUT.MAX_WIDTH} ${HOME_CONSTANTS.LAYOUT.PADDING}`}
            >
              {/* Breadcrumb */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <Breadcrumb pages={breadcrumbPages} />
                  <div className="text-sm text-gray-500">
                    {filteredApplications.length} {HOME_CONSTANTS.HEADER.COUNTER_TEXT}
                  </div>
                </div>
              </div>

              <ClientSearchFilters
                filters={clientFilters}
                onChange={(next) => {
                  setClientFilters(next);
                  if (next.pageSize !== appliedClientFilters.pageSize) setPage(1);
                  setAppliedClientFilters(next);
                  setPage(1);

                }}
                onSearch={() => {
                  setAppliedClientFilters(clientFilters);
                  setPage(1);
                }}
                onClear={() => {
                  const clear: ClientSearchFiltersState = {
                    ...clientFilters,
                    applicationType: isDccmRole
                      ? "AllCustomerCare"
                      : isDmRole
                        ? "AllConstruction"
                        : isCCM3Role
                          ? "AllCCM3Types"
                          : lockedApplicationType ?? "All",
                    status: isDccmRole ? "Bonus_Recipient_Review" : "All",
                    bonusRecipient: "",
                    region: "All",
                    pageSize: 25,
                    year: "All",
                    quarter: "All",
                  };
                  localStorage.removeItem(HOME_FILTERS_KEY);
                  setClientFilters(clear);
                  setAppliedClientFilters(clear);
                  setFilters({ year: "All", quarter: "All" });
                  setPage(1);
                }}
                isLoading={isLoading}
                selectedRole={selectedRole}
                applicationTypeOptions={
                  isDccmRole ? dccmApplicationTypeOptions : isDmRole ? dmApplicationTypeOptions : undefined
                }
                statusOptions={isDccmRole ? dccmMyApplicationsStatusOptions : undefined}
                statusMode="all"
              />

              {/* Content */}
              {isLoading ? (
                <div className="flex justify-center mt-8">
                  <CircularProgress />
                </div>
              ) : error ? (
                <div className="text-red-500 text-center mt-8">
                  {error instanceof Error
                    ? error.message
                    : `${HOME_CONSTANTS.STATES.ERROR.MESSAGE}${error as string}`}
                </div>
              ) : filteredApplications.length === 0 ? (
                <div className="text-gray-500 text-center p-8 bg-gray-50 rounded-lg mt-8">
                  <ClipboardDocumentIcon
                    className={`${HOME_CONSTANTS.STATES.EMPTY.ICON.SIZE} ${HOME_CONSTANTS.STATES.EMPTY.ICON.COLOR} mx-auto mb-4`}
                  />
                  <p className="text-lg font-medium">
                    {HOME_CONSTANTS.STATES.EMPTY.TITLE}
                  </p>
                  <p className="text-sm">
                    {HOME_CONSTANTS.STATES.EMPTY.SUBTITLE}
                  </p>
                </div>
              ) : (
                <div className={HOME_CONSTANTS.LAYOUT.SPACING.CONTENT}>
                  {paginatedApplications.map((app: BonusApplication) => (
                    <ApplicationItem
                      key={app.bonusApplicationId}
                      app={app}
                      expandedRows={expandedRows}
                      toggleRowExpansion={toggleRowExpansion}
                      roleType={RoleType[selectedRole.replace("-", "_") as keyof typeof RoleType]}
                      sourcePath="/my-applications"
                    />
                  ))}
                </div>
              )}
              <ClientPagination
                page={page}
                pageSize={appliedClientFilters.pageSize}
                totalCount={filteredApplications.length}
                onPageChange={setPage}
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Home;